'use client';

import { type ForwardRefExoticComponent, Fragment, forwardRef } from 'react';

import { isEmptyReactNode } from '../../utils/is-empty-react-node';
import { Slottable } from '../slot';
import { VisuallyHidden } from '../visually-hidden/visually-hidden';
import { IconButtonIcon } from './icon-button-icon';
import { IconButtonLoading } from './icon-button-loading';
import { IconButtonRoot } from './icon-button-root';
import type { IconButtonPropsInterface } from './types/icon-button.types';

/**
 * @since 8.42.0
 */

export const IconButton = forwardRef<HTMLButtonElement, IconButtonPropsInterface>(
  (props: IconButtonPropsInterface, forwardedRef) => {
    const { children, icon, loading = false, title, ...rest } = props;

    return (
      <IconButton.Root ref={forwardedRef} title={title} loading={loading} {...rest}>
        <Slottable child={children}>
          {(slottable) => (
            <Fragment>
              <VisuallyHidden>{title}</VisuallyHidden>
              <IconButton.Icon loading={loading}>
                {isEmptyReactNode(slottable) ? icon : slottable}
              </IconButton.Icon>
              {loading ? <IconButton.Loading /> : null}
            </Fragment>
          )}
        </Slottable>
      </IconButton.Root>
    );
  },
) as ForwardRefExoticComponent<IconButtonPropsInterface> & {
  Root: typeof IconButtonRoot;
  Icon: typeof IconButtonIcon;
  Loading: typeof IconButtonLoading;
};

IconButton.displayName = 'IconButton';

IconButton.Root = IconButtonRoot;
IconButton.Icon = IconButtonIcon;
IconButton.Loading = IconButtonLoading;
